import { CommonModule } from '@angular/common';
import { Component, effect, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { IUser } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { AppNotification, NotificationService } from '../../services/notification.service';
import { PageTitleService } from '../../services/page-title.service';
import { SidebarService } from '../../services/sidebar.service';
import { SocketService } from '../../services/socket.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() currentUser: IUser | null = null;
  private subscription = new Subscription();
  public isUserMenuOpen = false;
  public isSidebarCollapsed = false;
  public isNotificationMenuOpen = false;
  public notifications: AppNotification[] = [];
  public unreadCount = 0;
  public pageTitle = '';

  // Base title derived from the route only; combined with PageTitleService's suffix (set by the
  // current page, e.g. the tempCustomer of an Auftragsbegleitschein) into pageTitle below.
  private readonly baseTitle = signal('');

  constructor(
    public authService: AuthService,
    public router: Router,
    private sidebarService: SidebarService,
    private notificationService: NotificationService,
    private socketService: SocketService,
    private pageTitleService: PageTitleService
  ) {
    effect(() => {
      const base = this.baseTitle();
      const suffix = this.pageTitleService.suffix();
      this.pageTitle = suffix ? `${base} ${suffix}` : base;
    });
  }

  public ngOnInit(): void {
    // Connect to WebSocket
    this.socketService.connect();

    // Subscribe to route changes to close menus when navigating
    this.subscription.add(
      this.router.events.subscribe(() => {
        this.isUserMenuOpen = false;
        this.isNotificationMenuOpen = false;
      })
    );

    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.baseTitle.set(this.getPageTitle(event.urlAfterRedirects));
        }
      })
    );

    this.baseTitle.set(this.getPageTitle(this.router.url));

    // Subscribe to sidebar state
    this.subscription.add(
      this.sidebarService.isCollapsed$.subscribe(collapsed => {
        this.isSidebarCollapsed = collapsed;
      })
    );

    // Subscribe to notifications
    this.subscription.add(
      this.notificationService.notifications$.subscribe((notifications: AppNotification[]) => {
        this.notifications = notifications.slice(0, 10); // Show last 10
        this.unreadCount = this.notificationService.getUnreadCount();
      })
    );
  }

  /**
   * Toggle sidebar collapsed state
   */
  public toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  /**
   * Toggle user dropdown menu
   */
  public toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  public closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  /**
   * Logout the current user
   */
  public logout(): void {
    this.authService.logout();
  }

  /**
   * Toggle notification dropdown menu
   */
  public toggleNotificationMenu(): void {
    this.isNotificationMenuOpen = !this.isNotificationMenuOpen;
    if (this.isNotificationMenuOpen) {
      this.isUserMenuOpen = false;
    }
  }

  /**
   * Mark notification as read
   */
  public markAsRead(notification: AppNotification): void {
    this.notificationService.markAsRead(notification.id);
  }

  public onNotificationClick(notification: AppNotification): void {
    this.notificationService.markAsRead(notification.id);
    if (notification.data?.id && notification.data.action !== 'deleted') {
      this.router.navigate(['/delivery-notes/edit', notification.data.id]);
    }
    this.isNotificationMenuOpen = false;
  }

  public closeNotificationMenu(): void {
    this.notificationService.markAllAsRead();
    this.isNotificationMenuOpen = false;
  }

  /**
   * Mark all notifications as read
   */
  public markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  /**
   * Get notification icon based on type
   */
  public getNotificationIcon(type: AppNotification['type']): string {
    switch (type) {
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  }

  /**
   * Get notification icon color based on type
   */
  public getNotificationIconClass(type: AppNotification['type']): string {
    switch (type) {
      case 'success':
        return 'text-emerald-400';
      case 'warning':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-blue-400';
    }
  }

  /**
   * Format notification time
   */
  public formatNotificationTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min.`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `vor ${hours} Std.`;

    return new Date(date).toLocaleDateString('de-DE');
  }

  private getPageTitle(url: string): string {
    const clean = (url || '').split('?')[0].split('#')[0];

    if (clean.startsWith('/order-documents/new')) return 'Auftragsbegleitschein erstellen';
    if (clean.startsWith('/order-documents')) return 'Auftragsbegleitschein';
    if (clean.startsWith('/delivery-notes/new')) return 'Lieferzettel erstellen';
    if (clean.startsWith('/delivery-notes/edit/')) return 'Lieferzettel bearbeiten';
    if (clean.startsWith('/delivery-notes')) return 'Lieferzettel';
    if (clean.startsWith('/delivery-dates')) return 'Liefertermine';
    if (clean.startsWith('/orders')) return 'Aufträge';
    if (clean.startsWith('/users')) return 'Benutzer';
    if (clean.startsWith('/text-blocks')) return 'Textbausteine';
    if (clean.startsWith('/deliver-note-statistics')) return 'Statistiken';
    return '';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
